// ═══════════════════════════════════════════════════════════════
// MainStreet — Damage Reports (Submit + Admin List)
// Merged DamageReport + AdminDamageReports
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Textarea,
  Select, MultiSelect, Modal,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus, IconRefresh, IconAlertTriangle,
} from '@tabler/icons-react';
import { useMainStreetOpsStore } from '../../store/mainstreetOpsStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { OP_STATUS, DAMAGE_TYPES } from './constants';
import { fmtDate } from '../../lib/dateFormat';

// ── Helpers ──────────────────────────────────────────────────

const OP_SELECT = OP_STATUS.map(s => ({ value: s.id, label: s.label }));
const DAMAGE_SELECT = DAMAGE_TYPES.map(d => ({ value: d.id, label: d.label }));

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
  { value: 'severe', label: 'Severe' },
  { value: 'destroyed', label: 'Destroyed' },
];

const SEVERITY_COLORS: Record<string, string> = {
  minor: 'green',
  moderate: 'yellow',
  major: 'orange',
  severe: 'red',
  destroyed: 'dark',
};

function opLabel(id: string): string {
  return OP_STATUS.find(s => s.id === id)?.label ?? id;
}

function damageLabel(id: string): string {
  return DAMAGE_TYPES.find(d => d.id === id)?.label ?? id;
}

// ═════════════════════════════════════════════════════════════
export default function DamageReports() {
  const { canEdit } = useModulePermission('mainstreet');
  const {
    damageReports, loading,
    loadDamageReports, createDamageReport,
  } = useMainStreetOpsStore();
  const { organizations, loadOrganizations } = useMainStreetStore();
  const { activeIncident, user } = useAuthStore();

  // Create modal
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [orgId, setOrgId] = useState('');
  const [opStatus, setOpStatus] = useState('');
  const [damageTypes, setDamageTypes] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('');
  const [saving, setSaving] = useState(false);

  // Filters
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  useEffect(() => {
    loadDamageReports();
    loadOrganizations();
  }, []);

  const orgOptions = useMemo(() =>
    organizations.map(o => ({ value: String(o.id), label: o.name })),
    [organizations],
  );

  const orgFilterOptions = useMemo(() => [
    { value: 'all', label: 'All Organizations' },
    ...orgOptions,
  ], [orgOptions]);

  const sevFilterOptions = [
    { value: 'all', label: 'All Severities' },
    ...SEVERITY_OPTIONS,
  ];

  const filtered = useMemo(() => {
    let list = damageReports;
    if (activeIncident) {
      list = list.filter(r => String(r.incidentId) === String(activeIncident.id));
    }
    if (filterOrg !== 'all') list = list.filter(r => String(r.orgId) === filterOrg);
    if (filterSeverity !== 'all') list = list.filter(r => r.severity === filterSeverity);
    return [...list].sort((a, b) => (b.reportedAt ?? '').localeCompare(a.reportedAt ?? ''));
  }, [damageReports, activeIncident, filterOrg, filterSeverity]);

  function orgName(id: string): string {
    return organizations.find(o => String(o.id) === id)?.name ?? id;
  }

  // ── Create ─────────────────────────────────────────────────

  async function handleCreate() {
    if (!orgId || !severity || !opStatus) {
      notifications.show({ color: 'red', title: 'Required', message: 'Organization, operational status, and severity are required.' });
      return;
    }
    const org = organizations.find(o => String(o.id) === orgId);
    setSaving(true);
    await createDamageReport({
      orgId,
      orgName: org?.name ?? '',
      incidentId: activeIncident ? String(activeIncident.id) : '',
      opStatus,
      damageTypes: damageTypes.join(','),
      description: description.trim(),
      severity,
      reportedBy: user?.fullName ?? user?.email ?? 'Unknown',
      reportedAt: new Date().toISOString(),
    });
    setSaving(false);
    notifications.show({ color: 'green', title: 'Created', message: 'Damage report submitted.' });
    setOrgId(''); setOpStatus(''); setDamageTypes([]); setDescription(''); setSeverity('');
    closeCreate();
  }

  // ── Render ─────────────────────────────────────────────────

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Damage Reports</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => loadDamageReports()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Submit Report
          </Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <Select data={orgFilterOptions} value={filterOrg}
          onChange={v => setFilterOrg(v ?? 'all')} size="xs" w={220} searchable />
        <Select data={sevFilterOptions} value={filterSeverity}
          onChange={v => setFilterSeverity(v ?? 'all')} size="xs" w={180} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} report{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Organization</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Op Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Damage Types</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Severity</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Reporter</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loading ? 'Loading...' : 'No damage reports found'}
                  </td>
                </tr>
              )}
              {filtered.map(report => (
                <tr key={report.id}
                  style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                    {report.orgName || orgName(report.orgId)}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {report.opStatus ? (
                      <Badge size="xs" color={OP_STATUS.find(s => s.id === report.opStatus)?.color ?? 'gray'}>
                        {opLabel(report.opStatus)}
                      </Badge>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Group gap={4}>
                      {report.damageTypes
                        ? report.damageTypes.split(',').map(d => (
                            <Badge key={d} size="xs" variant="outline">{damageLabel(d.trim())}</Badge>
                          ))
                        : '-'}
                    </Group>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="xs" color={SEVERITY_COLORS[report.severity] ?? 'gray'}>
                      {report.severity || '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{report.reportedBy || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{fmtDate(report.reportedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* ── Create Modal ────────────────────────────────────── */}
      <Modal opened={createOpened} onClose={closeCreate} title="Submit Damage Report" size="lg">
        <Select label="Organization" data={orgOptions} value={orgId}
          onChange={v => setOrgId(v ?? '')} placeholder="Select organization"
          size="sm" mb="sm" searchable required />

        <Select label="Operational Status" data={OP_SELECT} value={opStatus}
          onChange={v => setOpStatus(v ?? '')} placeholder="Current status"
          size="sm" mb="sm" required />

        <MultiSelect label="Damage Types" data={DAMAGE_SELECT} value={damageTypes}
          onChange={setDamageTypes} placeholder="Select damage types"
          size="sm" mb="sm" searchable />

        <Select label="Severity" data={SEVERITY_OPTIONS} value={severity}
          onChange={v => setSeverity(v ?? '')} placeholder="Select severity"
          size="sm" mb="sm" required />

        <Textarea label="Description" value={description}
          onChange={e => setDescription(e.currentTarget.value)}
          placeholder="Describe the damage..." size="sm" mb="md" autosize minRows={3} />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
          <Button loading={saving} onClick={handleCreate}
            leftSection={<IconAlertTriangle size={16} />}>Submit Report</Button>
        </Group>
      </Modal>
    </div>
  );
}
